import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "./ui/Button";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Dialog } from "./ui/Dialog";
import { usePromoteAlternative } from "../hooks/usePromoteAlternative";
import { toast } from "../lib/toast";
import type { Draft, DraftAlternative } from "../lib/api";

interface Props {
  draft: Draft;
}

export const AlternativesPanel = ({ draft }: Props) => {
  const [open, setOpen] = useState(false);
  const [confirmPromote, setConfirmPromote] = useState<"A" | "B" | "C" | null>(null);
  const [viewing, setViewing] = useState<DraftAlternative | null>(null);
  const promote = usePromoteAlternative();

  const alts = draft.alternatives ?? [];
  if (alts.length === 0) return null;

  const scoreLine = `main ${draft.rankerScore?.toFixed(2) ?? "?"}${alts
    .map((a) => `, ${a.label} ${a.rankerScore.toFixed(2)}`)
    .join("")}`;

  return (
    <section className="rounded-2xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-sm font-semibold text-slate-700 dark:text-zinc-300"
        aria-expanded={open}
        aria-controls="alt-panel"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {alts.length} alternativa{alts.length > 1 ? "s" : ""} geradas
        </span>
        <span className="text-xs text-slate-500 dark:text-zinc-500 font-normal">{scoreLine}</span>
      </button>

      {open && (
        <div id="alt-panel" className="mt-4 space-y-3">
          {draft.rankerReason && (
            <p className="rounded-lg bg-slate-50 dark:bg-zinc-800 p-3 text-xs text-slate-600 dark:text-zinc-400">
              <span className="font-medium">Por que esta venceu:</span> {draft.rankerReason}
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {alts.map((a) => (
              <article
                key={a.label}
                aria-label={`Alternativa ${a.label}`}
                className="rounded-xl border border-slate-200 dark:border-zinc-800 p-3"
                data-testid={`alt-${a.label}`}
              >
                <header className="mb-2 flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                  <span>Alternativa {a.label} · score {a.rankerScore.toFixed(2)}</span>
                  <span>{a.format} · {a.theme}</span>
                </header>
                <p className="text-sm text-slate-900 dark:text-zinc-100 mb-2 line-clamp-3">{a.hook}</p>
                <p className="text-xs text-slate-600 dark:text-zinc-400 line-clamp-4 mb-3">
                  {a.body.slice(0, 300)}
                  {a.body.length > 300 && "..."}
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setViewing(a)}>
                    Ver completo
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setConfirmPromote(a.label)}>
                    Promover
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? `Alternativa ${viewing.label}` : ""}
        size="lg"
      >
        {viewing && (
          <>
            <pre className="whitespace-pre-wrap break-words text-sm">{viewing.content}</pre>
            <div className="mt-4 flex justify-end">
              <Button variant="secondary" onClick={() => setViewing(null)}>Fechar</Button>
            </div>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmPromote !== null}
        title={`Promover alternativa ${confirmPromote}?`}
        description="O texto atual vira a alternativa nesse slot. Você pode reverter promovendo de volta."
        confirmLabel="Promover"
        cancelLabel="Cancelar"
        destructive
        loading={promote.isPending}
        onConfirm={() => {
          if (confirmPromote) {
            promote.mutate(
              { draftId: draft.id, label: confirmPromote },
              {
                onSuccess: () => toast.success(`Alternativa ${confirmPromote} promovida`),
                onError: (err: Error) => toast.error("Falha ao promover", { description: err.message }),
                onSettled: () => setConfirmPromote(null),
              }
            );
          }
        }}
        onCancel={() => setConfirmPromote(null)}
      />
    </section>
  );
};
